import {
  Layers,
  MonitorCog,
  Settings2,
  Upload,
} from 'lucide-react'
import { observer } from 'mobx-react'
import {
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  WorkflowPanel,
  type WorkflowPanelHandle,
} from '@maxus/website/components'
import { publicAssetUrl } from '@maxus/website/constants'
import { isDesktopTarget } from '@maxus/website/lib/desktop'
import { cn } from '@maxus/website/utils'

import { Breadcrumb } from './breadcrumb'
import { breadcrumbStore } from './breadcrumb-store'
import { DownloadBridge } from './download-bridge'
import { layoutStore } from './layout-store'
import { User } from './user'

export { breadcrumbStore } from './breadcrumb-store'

type AuthedPCLayoutProps = {
  children?: ReactNode
}

const menuItems = [
  { key: 'projects', label: 'Projects', Icon: Layers },
  { key: 'settings', label: 'Console', Icon: MonitorCog },
  { key: 'preference', label: 'Preferences', Icon: Settings2 },
] as const

export const AuthedPCLayout = observer(({ children }: AuthedPCLayoutProps) => {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()

  const activePathKey = menuItems.find(({ key }) => location.pathname.includes(key))?.key ?? 'projects'
  const [isUploadActive, setIsUploadActive] = useState(false)
  const uploadPanelRef = useRef<WorkflowPanelHandle>(null)

  useEffect(
    () => {
      uploadPanelRef.current?.close()
      setIsUploadActive(false)
    },
    [location.pathname],
  )

  const handleUploadClick = () => {
    if (isUploadActive) {
      uploadPanelRef.current?.close()
    } else {
      uploadPanelRef.current?.open()
      setIsUploadActive(true)
    }
  }

  return (
    <div className="w-screen h-screen overflow-hidden flex">
      {/* side */}
      <aside className="box-border py-4.5 w-20 h-full grow-0 shrink-0 flex flex-col justify-between bg-surface-chrome">
        {/* side - top */}
        <div className="box-border px-5 w-full flex flex-col gap-7.5">
          {isDesktopTarget ? (
            <div aria-hidden="true" className="size-10" />
          ) : (
            <img alt="Maxus" className="size-10 rounded-lg object-contain" src={publicAssetUrl('logo.png')} />
          )}

          {/* menu */}
          <nav className="flex flex-col gap-5">
            {menuItems.map(({ key, label, Icon }) => {
              const active = activePathKey === key

              return (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      aria-label={t(label)}
                      className={cn(
                        'grid size-10 place-items-center rounded-lg transition hover:bg-surface-panel hover:text-text-secondary',
                        active ? 'bg-surface-active text-text-primary' : 'text-text-tertiary',
                      )}
                      onClick={() => {
                        breadcrumbStore.crumbs = [{ key, label: t(label), to: `/${key}` }]
                        setIsUploadActive(false)
                        navigate(`/${key}`)
                      }}
                      type="button"
                    >
                      <Icon className="size-6" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right">{t(label)}</TooltipContent>
                </Tooltip>
              )
            })}
          </nav>
        </div>

        {/* side - bottom */}
        <div className="w-full flex flex-col items-center gap-5">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                aria-label={t('Upload')}
                className={cn(
                  'grid size-10 place-items-center rounded-lg transition hover:bg-surface-panel hover:text-text-secondary',
                  isUploadActive ? 'bg-surface-active text-text-primary' : 'text-text-tertiary',
                )}
                onClick={handleUploadClick}
                type="button"
              >
                <Upload className="size-6" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">{t('Upload')}</TooltipContent>
          </Tooltip>
          <DownloadBridge />
          <User />
        </div>
      </aside>

      {/* main */}
      <div className="grow shrink h-full overflow-hidden flex flex-col">
        {/* main - header */}
        <div className="grow-0 shrink-0 box-border px-6 h-15 w-full flex items-center overflow-hidden bg-surface-panel">
          <Breadcrumb />
        </div>

        {/* main - content */}
        <div
          className={cn(
            'grow shrink box-border h-full w-full overflow-auto bg-bg-canvas',
            isDesktopTarget && '!bg-transparent',
            !layoutStore.disablePadding && 'p-6 pt-3.75',
          )}
        >
          {children}
        </div>
      </div>

      {/* upload / workflow 管理面板：由侧栏底部 Upload 入口从左侧唤出 */}
      <WorkflowPanel onClose={() => setIsUploadActive(false)} ref={uploadPanelRef} />
    </div>
  )
})
